"use client";

import { useMemo } from "react";
import Link from "next/link";
import { motion } from "framer-motion";
import { Badge } from "~/components/ui/badge";
import { User, MessageSquare, BookOpen, Wrench, Inbox, Contact, MapPin, Users } from "lucide-react";
import { getMarketingChrome } from "~/lib/siteglue/marketing-chrome";
import { adaptMarketingCopyForBrand } from "~/lib/siteglue/adapt-marketing-copy";

// After copying assets to public/images/, replace these strings with next/image imports:
// import Image from "next/image";
const IMG = {
  inbox:     "/images/platform-inbox.png",
  contacts:  "/images/platform-contacts.png",
  locations: "/images/platform-locations.png",
  team:      "/images/platform-team.png",
  identity:  "/images/platform-identity.png",
  skills:    "/images/platform-skills.png",
  education: "/images/platform-education.png",
  tools:     "/images/platform-tools.png",
};

const sections = [
  {
    id: "inbox",
    icon: Inbox,
    tag: "Inbox",
    title: "Real-Time Conversation Management",
    description: "Monitor and manage all patient conversations from a unified inbox. Track total conversations, team handoffs, abandoned chats, and conversion rates at a glance. Filter by channel, date range, and status. View full conversation transcripts with AI responses and seamlessly take over live chats when needed.",
    image: IMG.inbox,
    features: ["Unified inbox across all channels", "Real-time conversation transcripts", "One-click live chat takeover", "Conversion rate & engagement analytics"],
  },
  {
    id: "contacts",
    icon: Contact,
    tag: "Contacts",
    title: "Patient Contact Management",
    description: "Automatically capture and organize every patient contact. Track new vs. existing clients, engagement scores, and communication history. Search and filter contacts by type, channel, location, and date. View detailed reports for each contact to understand their full journey.",
    image: IMG.contacts,
    features: ["Automatic contact capture from all channels", "New client vs. existing client classification", "Engagement scoring & trend tracking", "Per-contact conversation reports"],
  },
  {
    id: "locations",
    icon: MapPin,
    tag: "Locations",
    title: "Multi-Location Practice Support",
    description: "Configure and manage multiple practice locations with dedicated team members for each. Set default locations, assign staff, and ensure patients connect with the right office. Inbound GPT intelligently routes conversations based on patient location and practice availability.",
    image: IMG.locations,
    features: ["Unlimited practice locations", "Per-location team member assignments", "Intelligent patient-to-location routing", "Default location configuration"],
  },
  {
    id: "team",
    icon: Users,
    tag: "Team",
    title: "Team Member Management",
    description: "Add and manage team members with specific roles and permissions. Configure email notifications for conversation summaries, start alerts, and dropped conversation reports. Assign team members across multiple locations and designate lead team members for each office.",
    image: IMG.team,
    features: ["Role-based team member management", "Customizable email notification preferences", "Multi-location team assignments", "Lead team member designation"],
  },
  {
    id: "identity",
    icon: User,
    tag: "Identity",
    title: "Give Inbound GPT a Personality",
    description: "Create a named Inbound GPT with a custom bio, communication tone, and personality traits. Configure language settings, training data sources, and how Inbound GPT presents itself to patients. Fully customizable to match your practice's brand and bedside manner.",
    image: IMG.identity,
    features: ["Custom agent name & bio", "Personality traits & empathy levels", "Multi-language support with auto-translation", "Training data source configuration"],
  },
  {
    id: "skills",
    icon: MessageSquare,
    tag: "Skills",
    title: "Multi-Channel Patient Communication",
    description: "Deploy your AI across website chat, browser calls, phone, SMS, contact forms, WhatsApp, and Facebook Messenger. Each channel is independently configurable with its own AI model, welcome messages, and agent instructions. Enable team member takeover for seamless human escalation.",
    image: IMG.skills,
    features: ["Website chat, phone, SMS & WhatsApp", "Team member takeover capability", "Custom welcome messages per channel", "Claude 3.5 Sonnet & more AI models"],
  },
  {
    id: "education",
    icon: BookOpen,
    tag: "Education",
    title: "Train Inbound GPT with Practice Knowledge",
    description: "Build a knowledge base from your practice's content. Smart updates keep Inbound GPT current, while RAG integration with vector embeddings enables semantic search across your documents. Inbound GPT gets smarter with every interaction and content update.",
    image: IMG.education,
    features: ["Primary knowledge base with smart updates", "RAG integration with vector search", "Automatic web content crawling", "Content summary & analytics"],
  },
  {
    id: "tools",
    icon: Wrench,
    tag: "Tools",
    title: "300+ Healthcare Integrations via MCP",
    description: "Connect to your existing practice management systems through Keragon's MCP protocol. Integrate with Juvonno, NexHealth, Athenahealth, ModMed, Healthie, and hundreds more. Manage appointments, scheduling, website deployment, and Google Business Profile — all from one platform.",
    image: IMG.tools,
    features: ["Juvonno, NexHealth & Keragon MCP", "Appointments & scheduling management", "Website & landing page deployment", "Google Business Profile integration"],
  },
];

export function Platform() {
  const brand = getMarketingChrome().brandId;
  const a = (s: string) => adaptMarketingCopyForBrand(s, brand);
  const adaptedSections = useMemo(
    () =>
      sections.map((section) => ({
        ...section,
        title: a(section.title),
        description: a(section.description),
        features: section.features.map(a),
      })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [brand],
  );

  return (
    <section id="platform" data-testid="section-platform" className="relative py-24 sm:py-32">
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-border to-transparent" />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-20">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-3xl sm:text-4xl font-bold tracking-tight"
          >
            {a("Manage Inbound GPT from one, easy to use")}{" "}
            <span className="text-primary">Dashboard</span>
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="mt-4 text-lg text-muted-foreground max-w-2xl mx-auto"
          >
            {a(
              "Configure Inbound GPT's identity, communication channels, knowledge base, and integrations — all in one place.",
            )}
          </motion.p>
        </div>

        <div className="flex flex-wrap justify-center gap-2 mb-16">
          {adaptedSections.map((section) => (
            <a
              key={section.id}
              href={`#platform-${section.id}`}
              onClick={(e) => {
                e.preventDefault();
                document.getElementById(`platform-${section.id}`)?.scrollIntoView({ behavior: "smooth" });
              }}
              className="px-4 py-2 text-sm font-medium rounded-md border border-border bg-card hover:bg-primary/10 hover:border-primary/30 hover:text-primary transition-colors"
              data-testid={`link-platform-nav-${section.id}`}
            >
              {section.tag}
            </a>
          ))}
          <Link
            href="/pricing"
            className="px-4 py-2 text-sm font-medium rounded-md border border-border bg-card hover:bg-primary/10 hover:border-primary/30 hover:text-primary transition-colors"
            data-testid="link-platform-nav-pricing"
          >
            Pricing
          </Link>
        </div>

        <div className="space-y-24">
          {adaptedSections.map((section, index) => {
            const isReversed = index % 2 === 1;
            return (
              <motion.div
                id={`platform-${section.id}`}
                key={section.id}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-100px" }}
                transition={{ duration: 0.5 }}
                className={`scroll-mt-24 flex flex-col ${isReversed ? "lg:flex-row-reverse" : "lg:flex-row"} items-center gap-10 lg:gap-16`}
              >
                <div className="flex-1 w-full lg:w-auto">
                  <div className="flex items-center gap-2 mb-4 flex-wrap">
                    <div className="w-10 h-10 rounded-md bg-primary/10 flex items-center justify-center">
                      <section.icon className="w-5 h-5 text-primary" />
                    </div>
                    <Badge variant="secondary" className="text-xs">{section.tag}</Badge>
                  </div>
                  <h3 className="text-2xl sm:text-3xl font-bold tracking-tight mb-4" data-testid={`text-platform-${section.id}`}>
                    {section.title}
                  </h3>
                  <p className="text-muted-foreground leading-relaxed mb-6">{section.description}</p>
                  <ul className="space-y-3">
                    {section.features.map((feature) => (
                      <li key={feature} className="flex items-start gap-3 text-sm">
                        <div className="w-5 h-5 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                          <div className="w-1.5 h-1.5 rounded-full bg-primary" />
                        </div>
                        <span className="text-muted-foreground">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="flex-1 w-full lg:w-auto">
                  <div className="rounded-md border border-border bg-card p-2">
                    {/* Replace with next/image after copying assets to public/images/ */}
                    <img
                      src={section.image}
                      alt={section.title}
                      loading="lazy"
                      className="w-full rounded-sm"
                      data-testid={`img-platform-${section.id}`}
                    />
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
